import request from '../../../../api/request';
import { downloadFromBlob } from '../../../../utils/blob';
import {
  ChargeItem,
  CreateChargeBody,
  DebtFiltersState,
  DebtResponse,
  PaymentLinkResponse,
} from '../Debt/types';

const CURRENT_ACCOUNT = {
  DEBTS: (studentId: string | number) =>
    `maintainers-portal/financial-portal/current-account/students/${studentId}/debts`,

  DOWNLOAD_VOUCHER: (studentId: string | number) =>
    `maintainers-portal/financial-portal/current-account/students/${studentId}/debts/download-voucher`,

  PAYMENT_LINK: (studentId: string | number, debtId: number | string) =>
    `maintainers-portal/financial-portal/current-account/students/${studentId}/debts/${debtId}/payment-link`,

  MULTI_PAYMENT_LINK: (studentId: string | number) =>
    `maintainers-portal/financial-portal/current-account/students/${studentId}/debts/payment-link`,

  CHARGE_ITEMS:
    'maintainers-portal/financial-portal/current-account/charges/items',

  CREATE_CHARGE: (studentId: string | number) =>
    `maintainers-portal/financial-portal/current-account/students/${studentId}/charges`,

  CURRENCIES: 'maintainers-portal/financial-portal/current-account/currencies',

  COST_CENTERS:
    'maintainers-portal/financial-portal/current-account/cost-centers',
};

export interface ChargeCostCenterOption {
  id: number;
  name: string;
  code: string;
}

export interface CurrencyOption {
  id: number;
  code: string;
  name: string;
}

// HU #15319 — serialize only the dimensions that are not null, so the query
// string stays clean (`?year=2026&period=1`) and the backend DTO's optional
// decorators can treat missing fields as "no filter".
function toDebtsQueryParams(
  filters?: DebtFiltersState | null,
): Record<string, string | number> | undefined {
  if (!filters) {
    return undefined;
  }
  const params: Record<string, string | number> = {};
  if (filters.year !== null) {
    params.year = filters.year;
  }
  if (filters.period !== null) {
    params.period = filters.period;
  }
  if (filters.studyPlanId !== null) {
    params.studyPlanId = filters.studyPlanId;
  }
  if (filters.status !== null) {
    params.status = filters.status;
  }
  return Object.keys(params).length > 0 ? params : undefined;
}

export function getDebts(
  studentId: string | number,
  filters?: DebtFiltersState | null,
) {
  const params = toDebtsQueryParams(filters);
  return request<DebtResponse>(CURRENT_ACCOUNT.DEBTS(studentId), {
    method: 'get',
    ...(params ? { params } : {}),
  });
}

export async function downloadVoucher({
  studentId,
  debtIds,
  amountToPay,
}: {
  studentId: string | number;
  debtIds: number[];
  amountToPay: number;
}) {
  const res = await request(CURRENT_ACCOUNT.DOWNLOAD_VOUCHER(studentId), {
    method: 'POST',
    data: { debtIds, amountToPay },
    responseType: 'blob',
  });
  const blob = new Blob([res.data], { type: 'application/pdf' });
  downloadFromBlob(blob, 'voucher.pdf');
}

export function generatePaymentLink(
  studentId: string | number,
  debtId: number | string,
) {
  return request<PaymentLinkResponse>(
    CURRENT_ACCOUNT.PAYMENT_LINK(studentId, debtId),
    { method: 'POST' },
  );
}

export function generateMultiPaymentLink(
  studentId: string | number,
  debtIds: (number | string)[],
) {
  return request<PaymentLinkResponse>(
    CURRENT_ACCOUNT.MULTI_PAYMENT_LINK(studentId),
    // El backend espera `debtIds` como strings (los ids de deuda son uuid; el
    // tipo `Debt.id` del front dice `number` por herencia, no por la API).
    { method: 'POST', data: { debtIds: debtIds.map((id) => String(id)) } },
  );
}

export function getChargeItems() {
  return request<ChargeItem[]>(CURRENT_ACCOUNT.CHARGE_ITEMS, {
    method: 'get',
  });
}

export function createCharge(studentId: number, body: CreateChargeBody) {
  return request<{ paymentAgreementId: string; debtId: string }>(
    CURRENT_ACCOUNT.CREATE_CHARGE(studentId),
    { method: 'POST', data: body },
  );
}

/**
 * Centros de costo vigentes. Antes las opciones se derivaban de los textos
 * distintos de los ítems de cargo; desde que el ítem apunta al catálogo, ese
 * texto ya no existe y hay que leer el catálogo.
 */
export function getChargeCostCenters() {
  return request<ChargeCostCenterOption[]>(CURRENT_ACCOUNT.COST_CENTERS, {
    method: 'get',
  });
}

export function getChargeCurrencies() {
  return request<CurrencyOption[]>(CURRENT_ACCOUNT.CURRENCIES, {
    method: 'get',
  });
}
